/**
 * Normalizes a string value specifically for duplicate check generation.
 * - Converts to lowercase.
 * - Removes accents/diacritics.
 * - Removes irrelevant punctuation (commas, periods, question marks, colons, semi-colons, brackets, quotes, etc.).
 * - Squashes consecutive spaces into a single space and trims.
 */
export function normalizeForDuplicateKey(value: string | null | undefined): string {
  if (!value) return "";
  
  // 1. Lowercase
  let normalized = value.toLowerCase();
  
  // 2. Remove accents/diacritics (French standard)
  normalized = normalized.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  
  // 3. Remove punctuation and irrelevant characters
  // Keeps alphanumeric and spaces
  normalized = normalized.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"'’\[\]\\]/g, "");
  
  // 4. Remove extra spaces and trim
  normalized = normalized.replace(/\s+/g, " ").trim();
  
  return normalized;
}

/**
 * Builds duplicate_key for Error Bank records
 * category + competence + section + originalSentence + correctedSentence + explanation
 */
export function buildErrorDuplicateKey(error: {
  category?: string;
  competency?: string;
  competence?: string;
  section?: string;
  original?: string;
  original_sentence?: string;
  correction?: string;
  corrected?: string;
  corrected_sentence?: string;
  explanation?: string;
}): string {
  const category = error.category || "";
  const competence = error.competency || error.competence || "writing";
  const section = error.section || "";
  const original = error.original || error.original_sentence || "";
  const correction = error.correction || error.corrected || error.corrected_sentence || "";
  const explanation = error.explanation || "";
  
  const combined = `${category}${competence}${section}${original}${correction}${explanation}`;
  return normalizeForDuplicateKey(combined);
}

/**
 * Builds duplicate_key for Word Bank records
 * language + word + translation + definition + exampleSentence
 */
export function buildWordDuplicateKey(word: {
  language?: string;
  word?: string;
  term?: string;
  translation?: string;
  meaning?: string;
  definition?: string;
  example_sentence?: string;
  exampleSentence?: string;
  context?: string;
}): string {
  const language = word.language || "fr";
  const text = word.word || word.term || "";
  const translation = word.translation || word.meaning || "";
  const definition = word.definition || word.meaning || "";
  const exampleSentence = word.example_sentence || word.exampleSentence || word.context || "";
  
  const combined = `${language}${text}${translation}${definition}${exampleSentence}`;
  return normalizeForDuplicateKey(combined);
}

/**
 * Builds duplicate_key for Flashcards
 * type + language + front + back + category
 */
export function buildFlashcardDuplicateKey(card: {
  type?: string;
  language?: string;
  front?: string;
  back?: string;
  category?: string;
}): string {
  const type = card.type || "text";
  const language = card.language || "fr";
  const front = card.front || "";
  const back = card.back || "";
  const category = card.category || "Geral";
  
  const combined = `${type}${language}${front}${back}${category}`;
  return normalizeForDuplicateKey(combined);
}

/**
 * Builds attempt_key for Test History
 */
export function buildTestAttemptKey(testAttemptId: string | null | undefined): string {
  if (!testAttemptId) return "";
  return testAttemptId.trim();
}

/**
 * Validates whether a given string is a valid UUID, returning the cleaned string or null.
 */
export function getValidUuidOrNull(id: any): string | null {
  if (typeof id !== "string") return null;
  const cleaned = id.trim();
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(cleaned) ? cleaned : null;
}

export function toNullableUuid(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(trimmed) ? trimmed : null;
}

/**
 * Checks if the given DB error represents a Unique Constraint violation (code 23505).
 */
export function isUniqueConstraintError(err: any): boolean {
  if (!err) return false;
  if (err.code === "23505") return true;
  
  const msg = String(err.message || err.details || "").toLowerCase();
  if (msg.includes("duplicate key") || msg.includes("unique constraint") || msg.includes("violates unique constraint")) {
    return true;
  }
  return false;
}

/**
 * Translates a duplicate error into a high-quality human-friendly warning message.
 */
export function getFriendlyDuplicateMessage(entity: "question" | "error" | "word" | "flashcard" | "history"): string {
  switch (entity) {
    case "question":
      return "Esta questão parece já estar cadastrada. Verifique o Banco de Questões antes de salvar novamente.";
    case "error":
      return "Este erro já está cadastrado no Banco de Erros.";
    case "word":
      return "Esta palavra/frase já está cadastrada no Banco de Palavras.";
    case "flashcard":
      return "Este flashcard já existe.";
    case "history":
      return "Este resultado de teste já foi registrado no histórico.";
    default:
      return "Este registro já existe no sistema.";
  }
}

/**
 * Intercepts, logs, and translates Supabase database errors.
 * If the error is an RLS policy violation (Postgres code 42501), it returns a friendly message.
 */
export function handleSupabaseError(err: any, entity: "question" | "error" | "word" | "flashcard" | "history"): Error {
  if (!err) return new Error("Erro desconhecido.");
  
  // Robust logging
  console.error(`[Supabase Error - ${entity}] Details:`, {
    message: err.message,
    code: err.code,
    details: err.details,
    hint: err.hint,
    original: err
  });

  // Check RLS Violation / Permission Denied (Postgres code 42501)
  if (err.code === "42501" || String(err.message || "").includes("row-level security") || String(err.message || "").toLowerCase().includes("permission denied")) {
    return new Error("Você não tem permissão para realizar esta ação ou sua sessão expirou.");
  }

  // Check Unique Constraint Duplicate
  if (isUniqueConstraintError(err)) {
    return new Error(getFriendlyDuplicateMessage(entity));
  }

  // Return standard error with original message
  return new Error(err.message || "Erro de comunicação com o banco de dados.");
}

